import { useId, useRef, type KeyboardEvent } from 'react'
import { Link } from 'react-router-dom'
import { cx } from './cx'
import { Icon } from './Icon'
import { Badge } from './Chips'

export interface TabItem {
  id: string
  label: string
  icon?: string | undefined
  badge?: number | undefined
  /** Con `to` la voce e' un <Link> (navigazione di rotta) con aria-current. */
  to?: string | undefined
}

export interface TabsProps {
  variant?: 'primary' | 'secondary' | undefined
  /** Larghezze uguali (M3: fixed se le voci sono al massimo 4). */
  fixed?: boolean | undefined
  items: readonly TabItem[]
  value: string
  onChange?: ((id: string) => void) | undefined
  'aria-label': string
  className?: string | undefined
}

/**
 * Tab: se tutte le voci hanno `to` e' un <nav> di link (aria-current="page"),
 * altrimenti un tablist con tabindex roving (frecce, Home, End).
 */
export function Tabs({ variant = 'primary', fixed, items, value, onChange, className, 'aria-label': ariaLabel }: TabsProps) {
  const uid = useId()
  const listRef = useRef<HTMLDivElement | null>(null)
  const routed = items.length > 0 && items.every((i) => i.to !== undefined)

  const inner = (item: TabItem) => (
    <>
      {item.icon ? <Icon name={item.icon} size={20} /> : null}
      <span>{item.label}</span>
      {item.badge !== undefined ? <Badge value={item.badge} /> : null}
    </>
  )

  if (routed) {
    return (
      <nav aria-label={ariaLabel} className={cx('md-tabs', `md-tabs--${variant}`, fixed && 'md-tabs--fixed', className)}>
        {items.map((item) => {
          const active = item.id === value
          return (
            <Link
              key={item.id}
              to={item.to as string}
              aria-current={active ? 'page' : undefined}
              className={cx('md-tab', active && 'md-tab--active', 'md-state')}
              onClick={() => onChange?.(item.id)}
            >
              {inner(item)}
            </Link>
          )
        })}
      </nav>
    )
  }

  const activeIndex = Math.max(0, items.findIndex((i) => i.id === value))

  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>, index: number) => {
    let next = -1
    if (e.key === 'ArrowRight') next = (index + 1) % items.length
    else if (e.key === 'ArrowLeft') next = (index - 1 + items.length) % items.length
    else if (e.key === 'Home') next = 0
    else if (e.key === 'End') next = items.length - 1
    if (next < 0) return
    e.preventDefault()
    const target = items[next]
    if (!target) return
    onChange?.(target.id)
    listRef.current?.querySelectorAll<HTMLButtonElement>('[role="tab"]')[next]?.focus()
  }

  return (
    <div ref={listRef} role="tablist" aria-label={ariaLabel} className={cx('md-tabs', `md-tabs--${variant}`, fixed && 'md-tabs--fixed', className)}>
      {items.map((item, i) => {
        const active = item.id === value
        return (
          <button
            key={item.id}
            id={`${uid}-tab-${item.id}`}
            type="button"
            role="tab"
            aria-selected={active}
            tabIndex={i === activeIndex ? 0 : -1}
            className={cx('md-tab', active && 'md-tab--active', 'md-state')}
            onClick={() => onChange?.(item.id)}
            onKeyDown={(e) => onKeyDown(e, i)}
          >
            {inner(item)}
          </button>
        )
      })}
    </div>
  )
}
